import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { type VariantProps, cn, cva } from "lib/cn";

export { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu";

const dropdownContent = cva("w-[200px] rounded-md bg-brand-polar p-2 shadow-none", {
    variants: {
        bordered: {
            true: "border border-brand-polarice",
            false: "",
        },
    },
    defaultVariants: {
        bordered: false,
    },
});

interface DropdownContentProps extends VariantProps<typeof dropdownContent> {
    arrow?: boolean;
    align?: "start" | "center" | "end";
    side?: "top" | "left" | "right" | "bottom";
    children: React.ReactNode;
    className?: string;
    /** Render into a body-level portal so the menu escapes the parent's
     *  stacking context. Needed inside modals, where sibling footer buttons
     *  otherwise paint over the menu. Off by default — existing call sites
     *  render in place and shouldn't move. */
    portal?: boolean;
}

export const DropdownContent = ({
    arrow = true,
    align = "center",
    bordered,
    side,
    children,
    className,
    portal = false,
}: DropdownContentProps) => {
    const content = (
        <DropdownMenu.Content
            sideOffset={5}
            align={align}
            side={side}
            className={dropdownContent({ bordered, className })}
        >
            {arrow && <DropdownMenu.Arrow className={cn("fill-brand-polar")} />}
            {children}
        </DropdownMenu.Content>
    );

    return portal ? <DropdownMenu.Portal>{content}</DropdownMenu.Portal> : content;
};

const dropdownItem = cva(
    cn(
        "radix-highlighted:outline-0",
        "flex cursor-pointer flex-row items-center rounded border-0 px-2",
        "text-[#424242] radix-highlighted:bg-[#2DA7E0] radix-highlighted:text-white",
    ),
    {
        variants: {
            size: {
                normal: "h-8 font-normal",
                smaller: "h-7 font-medium text-sm",
            },
        },
        defaultVariants: {
            size: "normal",
        },
    },
);

interface DropdownItemProps extends VariantProps<typeof dropdownItem> {
    children: React.ReactNode;
    className?: string;
    onSelect?: (event: Event) => void;
}

export const DropdownItem = ({ children, className, size, onSelect }: DropdownItemProps) => {
    return (
        <DropdownMenu.Item className={dropdownItem({ size, className })} onSelect={onSelect}>
            {children}
        </DropdownMenu.Item>
    );
};

export const Dropdown = ({
    children,
    content,
    modal = false,
    className,
    open,
    onOpenChange,
}: {
    children: React.ReactNode;
    content: React.ReactNode;
    className?: string;
    modal?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}) => {
    return (
        <div className={className}>
            <DropdownMenu.Root modal={modal} open={open} onOpenChange={onOpenChange}>
                <DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>
                {content}
            </DropdownMenu.Root>
        </div>
    );
};
